import { useEffect, useMemo, useState } from "react";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "@mui/material";

import { useCustomerId } from "../../Components/hooks/useCustomerId";
import { useAuthContext } from "../../Context/AuthContext";
import { useGetCustomerMPAs } from "../../Pages/MPA/hooks/useGetCustomerMPAs";
import { useIsPLESCustomer } from "../../Support/Components/TicketSteps/hooks";
import { consoleErrorWithSentry } from "../../utils";
import { useGetLoginUrl } from "../../utils/useGetLoginUrl";
import { filterAssetsByPlatform, useGroupedAssets } from "./hooks";
import { type CaseIQFormikValues } from "./types";

export const PLESButton = ({ caseIQValues }: { caseIQValues: CaseIQFormikValues }) => {
  const [awsLoginUrl, setAwsLoginUrl] = useState<string>("");

  const customerId = useCustomerId();
  const { platform, asset, priority } = caseIQValues;

  const isPlesCustomer = useIsPLESCustomer(customerId);
  const { isDoitEmployee } = useAuthContext();

  const getLoginUrl = useGetLoginUrl(false);

  const groupedAssets = useGroupedAssets();

  const assets = useMemo(() => filterAssetsByPlatform(groupedAssets, "amazon_web_services"), [groupedAssets]);
  const { customerMPAs, getAwsAssetMPAByAccountId } = useGetCustomerMPAs(false);

  useEffect(() => {
    setAwsLoginUrl("");
  }, [asset?.accountId]);

  const assetsWithMPA = useMemo(
    () =>
      assets
        .filter((cat) => !!cat.accountId)
        .map((cat) => ({
          ...cat,
          mpa: getAwsAssetMPAByAccountId(cat.accountId ?? "")?.masterPayerAccountId ?? "",
        }))
        .filter((awsAccount) => {
          if (!customerMPAs) {
            return false;
          }

          return customerMPAs.some((mpa) => mpa.accountNumber === awsAccount.mpa);
        }),
    [assets, customerMPAs, getAwsAssetMPAByAccountId]
  );

  const mpa = useMemo(
    () => assetsWithMPA.find((item) => item.accountId === asset?.accountId),
    [assetsWithMPA, asset?.accountId]
  );

  useEffect(() => {
    const getAWSTicket = async () => {
      try {
        if (
          asset?.accountId &&
          platform === "amazon_web_services" &&
          priority === "urgent" &&
          isPlesCustomer &&
          mpa?.mpa
        ) {
          const newAwsLoginUrl = await getLoginUrl(mpa?.mpa, false, customerId);
          if (newAwsLoginUrl !== "#") {
            setAwsLoginUrl(newAwsLoginUrl);
          }
        }
      } catch (error) {
        setAwsLoginUrl("");
        consoleErrorWithSentry(error);
      }
    };

    getAWSTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset?.accountId, customerId, isPlesCustomer, platform, priority, mpa?.mpa]);

  // Customer cannot use PLES if they have any assets connected to a shared MPA.
  // shared MPAs are deprecated, and no new ones will be created.

  const isMPAShared = useMemo(
    () =>
      assetsWithMPA.every(
        (item) =>
          (!!item.mpa && !["017920819041", "279843869311", "561602220360", "896363362129"].includes(item.mpa)) || true
      ),
    [assetsWithMPA]
  );

  if (
    platform === "amazon_web_services" &&
    priority === "urgent" &&
    isPlesCustomer &&
    isMPAShared &&
    asset &&
    awsLoginUrl
  ) {
    return (
      <Link
        href={isDoitEmployee ? "#" : awsLoginUrl}
        target="_blank"
        sx={{
          mx: 2,
          ...(isDoitEmployee && {
            pointerEvents: "none",
            opacity: 0.5,
            textDecoration: "none",
          }),
          textDecoration: "none",
          display: "flex",
          alignItems: "center",
        }}
      >
        Open this request directly with AWS (MPA: {mpa?.mpa}) <OpenInNewIcon fontSize="small" sx={{ ml: 0.5 }} />
      </Link>
    );
  }

  return null;
};
